import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Abas from "@radix-ui/react-tabs";
import { FileText, Ban, RefreshCw, RotateCcw, Search } from "lucide-react";
import type { BoletoDoPainel } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import {
  useAcaoSobreBoleto,
  useBoleto,
  useEventos,
  useReprocessarBoleto,
  useSondarBoleto,
  useTecnico,
  useVerificarBoleto,
  useVerPDF,
} from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { PainelLateral } from "@/componentes/PainelLateral";
import { PilulaDeSituacao } from "@/componentes/PilulaDeSituacao";
import { LinhaDigitavel } from "@/componentes/LinhaDigitavel";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { LinhaDoTempo } from "@/componentes/LinhaDoTempo";
import { BlocoDeCodigo } from "@/componentes/BlocoDeCodigo";
import { Botao } from "@/componentes/Botao";
import { Picote } from "@/componentes/Picote";
import { Dica } from "@/componentes/Dica";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao, type PedidoDeConfirmacao } from "@/componentes/Confirmacao";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { useDetalheDaRota } from "@/componentes/useDetalheDaRota";
import { ROTA_DOS_BOLETOS, rotaDaAuditoria, rotaDasEntregas } from "@/rotas";
import { banco, dataHora, dia, dinheiro, documento, idCurto } from "@/formato";
import {
  avisoDoEnvio,
  envioForaDoNormal,
  PossiveisDuplicidades,
  rotuloDaClasse,
  rotuloDoEnvio,
  TentativasDoEnvio,
} from "./envio";
import { esperaDaRecusa, fraseDaRecusa, fraseDoGate } from "./recusas";

/*
 * O detalhe do boleto — doc 06 §4.2 e doc 06b §3.
 *
 * Três abas: Resumo, Linha do tempo, Técnico. Rodapé fixo com as ações.
 *
 * Quem decide pílula, aviso e "Resolver" é o campo `indeterminado`: a definição mora na
 * coluna gerada do banco, e a tela não a recalcula. A verificação expirada é fato da
 * conciliação, e continua verdadeira no boleto resolvido à mão.
 *
 * O boleto da LISTA é o estado inicial: abrir o painel não deve mostrar um
 * esqueleto por meio segundo quando os dados já estão na tela atrás dele. Link
 * colado não tem a linha, e o painel espera a consulta por id. É ela que traz o
 * sacado sem máscara para quem tem papel de operação, e é o erro dela que aparece
 * no painel, com a lista utilizável atrás.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ REPROCESSAR É O GESTO MAIS PERIGOSO DO PAINEL.                           │
 * │                                                                          │
 * │ A confirmação é montada a cada render, e não guardada no clique: o       │
 * │ detalhe faz polling, e um boleto parecido que apareça com o diálogo      │
 * │ aberto precisa entrar na lista. A `key` da confirmação não muda, e o que │
 * │ o operador digitou fica.                                                 │
 * │                                                                          │
 * │ Os ids que vão no corpo são os da lista na tela, no render do clique: é  │
 * │ o que o operador declara ter conferido. O que nasceu depois fica de      │
 * │ fora, a API recusa com `possivel_duplicidade`, e a confirmação continua  │
 * │ aberta, com a lista relida e a justificativa intacta.                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function DetalheDoBoleto({
  id,
  boletoDaLista,
  aoFechar,
}: {
  id: string;
  boletoDaLista?: BoletoDoPainel | undefined;
  aoFechar: () => void;
}) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();

  const [aba, setAba] = useState("resumo");
  const [pedido, setPedido] = useState<PedidoDeConfirmacao | null>(null);
  const [reprocessando, setReprocessando] = useState(false);

  const { data: atualizado, error: erroDoBoleto } = useBoleto(cliente, id);
  const boleto = atualizado ?? boletoDaLista;
  const semBoleto = boleto === undefined && erroDoBoleto !== null;

  const { data: linhaDoTempo, error: erroDosEventos } = useEventos(cliente, semBoleto ? null : id);
  const podeOperar = pode("operador");
  const { data: tecnico, error: erroTecnico } = useTecnico(
    cliente,
    id,
    aba === "tecnico" && podeOperar,
  );

  const acao = useAcaoSobreBoleto(cliente, id);
  const reprocessar = useReprocessarBoleto(cliente, id);

  if (boleto === undefined) {
    return (
      <PainelLateral
        aberto
        aoFechar={aoFechar}
        descricao="Detalhe do boleto"
        titulo={
          semBoleto ? (
            <span className="font-semibold text-texto">Boleto</span>
          ) : (
            <Esqueleto className="h-5 w-40" />
          )
        }
      >
        <div aria-busy={!semBoleto} className="p-4">
          {semBoleto ? <FaixaDeErro erro={erroDoBoleto} /> : <Esqueleto className="h-40" />}
        </div>
      </PainelLateral>
    );
  }

  const referencia = boleto.referencia_externa ?? idCurto(id);
  const indeterminado = boleto.indeterminado;
  const parecidos = boleto.possiveis_duplicidades ?? [];
  const avisoDeEnvio = indeterminado ? undefined : avisoDoEnvio(boleto.envio_estado);

  async function executar(
    caminho: string,
    corpo: unknown,
    feito: string,
  ): Promise<void> {
    const resposta = await acao.mutateAsync({
      caminho,
      ...(corpo !== undefined ? { corpo } : {}),
    });
    setPedido(null);
    avisos.feito(feito, resposta?.aviso);
  }

  async function confirmarReprocesso(justificativa: string): Promise<void> {
    try {
      await reprocessar.mutateAsync({
        justificativa,
        parecidosConferidos: parecidos.map((parecido) => parecido.id),
      });
    } catch (falha) {
      // Fica no diálogo, com a frase nossa: a justificativa escrita não se perde.
      throw new Error(fraseDaRecusa(falha), { cause: falha });
    }
    setReprocessando(false);
    avisos.feito("Boleto de volta à fila", "O desfecho chega por evento.");
  }

  return (
    <>
      <PainelLateral
        aberto
        aoFechar={aoFechar}
        descricao={`Detalhe do boleto ${referencia}`}
        titulo={
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <PilulaDeSituacao situacao={boleto.situacao} indeterminado={indeterminado} />
              <span className="dados truncate text-corpo text-texto" title={referencia}>
                {referencia}
              </span>
            </div>
            <span className="text-titulo font-semibold tabular">
              {dinheiro(boleto.valor_centavos)}
            </span>
          </div>
        }
        rodape={
          <Acoes
            podeOperar={podeOperar}
            boleto={boleto}
            referencia={referencia}
            aoPedir={setPedido}
            aoReprocessar={() => setReprocessando(true)}
          />
        }
      >
        {/*
          A linha digitável no topo, em bloco próprio — o motivo de assinatura 1
          do doc 06a §3.6. É o número que o sacado tem na mão quando liga.
        */}
        {boleto.linha_digitavel ? (
          <div className="px-4 pt-3">
            <LinhaDigitavel valor={boleto.linha_digitavel} />
          </div>
        ) : null}

        {indeterminado && (
          <div className="mx-4 mt-3 rounded-controle border border-indeterminado/40 bg-indeterminado/10 px-3 py-2 text-[12px] text-texto">
            96 h sem confirmação do provider. Situação registrada, não confirmada:{" "}
            <span className="dados">{boleto.situacao}</span>.
          </div>
        )}

        {avisoDeEnvio !== undefined && (
          <div className="mx-4 mt-3 rounded-controle border border-indeterminado/40 bg-indeterminado/10 px-3 py-2 text-[12px] text-texto">
            {avisoDeEnvio}
          </div>
        )}

        <Abas.Root value={aba} onValueChange={setAba} className="mt-3">
          <Abas.List
            aria-label="Seções do boleto"
            className="flex gap-1 border-b border-fio px-4"
          >
            {[
              ["resumo", "Resumo"],
              ["linha-do-tempo", "Linha do tempo"],
              ["tecnico", "Técnico"],
            ].map(([valor, rotulo]) => (
              <Abas.Trigger
                key={valor}
                value={valor as string}
                className="-mb-px border-b-2 border-transparent px-2 py-2 text-corpo text-texto-2 data-[state=active]:border-esferografica data-[state=active]:text-texto"
              >
                {rotulo}
              </Abas.Trigger>
            ))}
          </Abas.List>

          <Abas.Content value="resumo" className="p-4">
            <div className="grid grid-cols-2 gap-2">
              <CaixaDeCampo rotulo="Sacado">{boleto.sacado_nome || null}</CaixaDeCampo>
              <CaixaDeCampo rotulo="Documento do sacado" dados>
                {documento(boleto.sacado_documento) || null}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Vencimento" dados>
                {dia(boleto.vencimento)}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Valor" dados>
                {dinheiro(boleto.valor_centavos)}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Aplicação">{boleto.sistema_nome || null}</CaixaDeCampo>
              <CaixaDeCampo rotulo="Banco">{banco(boleto.banco)}</CaixaDeCampo>
              <CaixaDeCampo rotulo="Convênio" dados>
                <span title={boleto.convenio_id}>{idCurto(boleto.convenio_id)}</span>
              </CaixaDeCampo>
              {boleto.nosso_numero !== undefined && (
                <CaixaDeCampo rotulo="Nosso número" dados>
                  {boleto.nosso_numero}
                </CaixaDeCampo>
              )}
              <CaixaDeCampo rotulo="Criado em" dados>
                {dataHora(boleto.criado_em)}
              </CaixaDeCampo>
              <CaixaDeCampo rotulo="Atualizado em" dados>
                {dataHora(boleto.atualizado_em)}
              </CaixaDeCampo>
              <div className="relative">
                <CaixaDeCampo rotulo="Última consulta" dados>
                  {boleto.ultima_consulta_em ? dataHora(boleto.ultima_consulta_em) : null}
                </CaixaDeCampo>
                <span className="absolute right-2 top-2 inline-flex">
                  <Dica texto="Só conta a consulta que o provider respondeu." />
                </span>
              </div>
              {boleto.verificacao_pedida_em ? (
                <CaixaDeCampo rotulo="Verificação pedida" dados>
                  {dataHora(boleto.verificacao_pedida_em)}
                </CaixaDeCampo>
              ) : null}
            </div>

            {boleto.sacado_mascarado && (
              /*
                O campo `sacado_mascarado` existe para isto: sem ele, a tela
                mostraria `529******25` sem que o operador soubesse se é máscara
                ou dado corrompido — e sem saber que existe mais para ver.
              */
              <p className="mt-3 flex items-center gap-1 text-[12px] text-texto-3">
                Sacado mascarado
                <Dica texto="A aba Técnico mostra o payload sem máscara; o acesso fica na auditoria." />
              </p>
            )}

            {envioForaDoNormal(boleto) && (
              <>
                <Picote className="my-4" />
                <EstadoDoEnvio boleto={boleto} />
              </>
            )}

            {parecidos.length > 0 && (
              <>
                <Picote className="my-4" />
                <PossiveisDuplicidades
                  parecidos={parecidos}
                  comLink
                  demais={boleto.reprocessamento_barrado_por === "possivel_duplicidade"}
                />
              </>
            )}

            {boleto.motivo_falha ? (
              <>
                <Picote className="my-4" />
                <BlocoDeCodigo rotulo="Motivo da falha" conteudo={boleto.motivo_falha} />
              </>
            ) : null}

            <Picote className="my-4" />
            <CaixaDeCampo rotulo="Identificador do boleto" dados>
              {boleto.id}
            </CaixaDeCampo>

            {/* A auditoria exige `operador`: a aba some para quem só lê. */}
            <div className="mt-3 flex gap-3 text-[12px]">
              <Link to={rotaDasEntregas({ boleto_id: boleto.id })} className={LINK}>
                Entregas
              </Link>
              {podeOperar && (
                <Link
                  to={rotaDaAuditoria({ entidade: "boleto", entidade_id: boleto.id })}
                  className={LINK}
                >
                  Auditoria
                </Link>
              )}
            </div>
          </Abas.Content>

          <Abas.Content value="linha-do-tempo">
            {erroDosEventos ? (
              <p role="alert" className="px-4 py-6 text-falha">
                {textoDoErro(erroDosEventos)}
              </p>
            ) : (
              <LinhaDoTempo eventos={linhaDoTempo?.eventos ?? []} />
            )}
          </Abas.Content>

          <Abas.Content value="tecnico" className="flex flex-col gap-4 p-4">
            {!podeOperar ? (
              /*
                A tela esconde o que o papel não alcança — mas quem autoriza é a
                API, que recusaria de qualquer forma e registraria a tentativa.
              */
              <p className="text-texto-2">Exige papel de operador.</p>
            ) : erroTecnico ? (
              <p role="alert" className="text-falha">
                {textoDoErro(erroTecnico)}
              </p>
            ) : (
              <>
                <p className="text-[12px] text-texto-3">Acesso registrado na auditoria.</p>

                <BlocoDeCodigo
                  rotulo="Payload original do cliente"
                  conteudo={tecnico?.payload_original}
                  vazio="O pedido não guardou payload."
                />

                <div className="grid grid-cols-2 gap-2">
                  <CaixaDeCampo rotulo="Provider">{tecnico?.provider || null}</CaixaDeCampo>
                  <CaixaDeCampo rotulo="Identificador no provider" dados>
                    {tecnico?.provider_id || null}
                  </CaixaDeCampo>
                </div>

                <TentativasDoEnvio tentativas={boleto.tentativas ?? []} />

                <BlocoDeCodigo
                  rotulo="Situação crua do provider"
                  conteudo={tecnico?.provider_situacao}
                  vazio={
                    tecnico?.tem_operacao_de_provider === false
                      ? "Nenhuma chamada ao provider."
                      : "O provider não devolveu situação."
                  }
                />

                <BlocoDeCodigo
                  rotulo="Trace"
                  conteudo={tecnico?.trace_id}
                  vazio="Sem trace registrado."
                />
              </>
            )}
          </Abas.Content>
        </Abas.Root>
      </PainelLateral>

      <Confirmacao
        pedido={reprocessando ? pedidoDeReprocesso(boleto, referencia) : pedido}
        aoCancelar={() => {
          setPedido(null);
          setReprocessando(false);
        }}
        aoConfirmar={async (justificativa) => {
          if (reprocessando) {
            await confirmarReprocesso(justificativa);
            return;
          }
          if (pedido === null) return;

          if (pedido.verbo === "Cancelar boleto") {
            await executar("cancelamento", undefined, "Cancelamento pedido");
            return;
          }
          if (pedido.verbo === "Resolver") {
            await executar(
              "resolucao",
              { situacao: boleto.situacao, justificativa },
              "Indeterminado resolvido",
            );
          }
        }}
      />
    </>
  );
}

const LINK = "text-esferografica underline-offset-2 hover:underline";

/** A API escreve a recusa em minúscula, porque o mesmo texto vai para o log. */
function recusaLegivel(falha: unknown): string {
  const texto = textoDoErro(falha);
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * A confirmação de reprocessar: `critica`, com a referência digitada e a justificativa.
 *
 * Nada aqui promete o nosso número: ele só se mantém quando está preso ao boleto, e o aviso
 * vale para quem achou que o título não existe, e está errado. A lista vem antes dos campos: é
 * o que o operador precisa ler antes de decidir.
 */
function pedidoDeReprocesso(boleto: BoletoDoPainel, referencia: string): PedidoDeConfirmacao {
  return {
    severidade: "critica",
    titulo: `Reprocessar o boleto ${referencia}?`,
    corpo: (
      <div className="flex flex-col gap-3">
        <p>Volta à fila e emite de novo. Se o título existir, é a segunda cobrança do sacado.</p>
        <PossiveisDuplicidades
          parecidos={boleto.possiveis_duplicidades ?? []}
          confirmando
          demais={boleto.reprocessamento_barrado_por === "possivel_duplicidade"}
        />
      </div>
    ),
    verbo: "Reprocessar",
    digitarParaLiberar: referencia,
    exigeJustificativa: true,
    identidade: boleto.id,
  };
}

/*
 * O envio, no resumo. O rótulo é o estado, e a tela não anuncia o que é rotina: `confirmado`
 * nunca chega aqui. Contagem e instante só aparecem quando existem: uma caixa com travessão diria
 * "perguntamos, e não há", e sonda que ninguém pediu não foi pergunta.
 */
function EstadoDoEnvio({ boleto }: { boleto: BoletoDoPainel }) {
  const sondasNegativas = boleto.sondas_negativas ?? 0;

  return (
    <div className="grid grid-cols-2 gap-2">
      <CaixaDeCampo rotulo="Envio">{rotuloDoEnvio(boleto.envio_estado)}</CaixaDeCampo>
      {boleto.envio_tentativas !== undefined && (
        <CaixaDeCampo rotulo="Tentativas" dados>
          {boleto.envio_tentativas}
        </CaixaDeCampo>
      )}
      {boleto.falha_classe !== undefined && (
        <CaixaDeCampo rotulo="Classe da falha">{rotuloDaClasse(boleto.falha_classe)}</CaixaDeCampo>
      )}
      {sondasNegativas > 0 && (
        <CaixaDeCampo rotulo="Sondas sem título" dados>
          {sondasNegativas}
        </CaixaDeCampo>
      )}
      {boleto.sonda_pedida_em !== undefined && (
        <CaixaDeCampo rotulo="Sonda pedida" dados>
          {dataHora(boleto.sonda_pedida_em)}
        </CaixaDeCampo>
      )}
      {boleto.sonda_sem_titulo_em !== undefined && (
        <CaixaDeCampo rotulo="Sem título em" dados>
          {dataHora(boleto.sonda_sem_titulo_em)}
        </CaixaDeCampo>
      )}
    </div>
  );
}

/**
 * Até quando a API mandou esperar, e o relógio que libera o botão.
 *
 * `sonda_em_recarga` chega com até 15 segundos de atraso, e outro operador pode ter pedido nesse
 * meio tempo. O `Retry-After` é a palavra final, e o clique seguinte não leva a mesma recusa.
 */
function useEsperaDaRecusa() {
  const [ate, setAte] = useState<number | null>(null);

  useEffect(() => {
    if (ate === null) return;
    const relogio = setTimeout(() => setAte(null), Math.max(ate - Date.now(), 0));
    return () => clearTimeout(relogio);
  }, [ate]);

  return {
    esperando: ate !== null,
    esperar: (segundos: number) => setAte(Date.now() + segundos * 1000),
  };
}

/** `/boletos/:id`: o painel sobre a lista, com a linha dela como estado inicial. */
export function BoletoDaRota() {
  const { id, linha, fechar } = useDetalheDaRota<BoletoDoPainel>(
    (busca) => `${ROTA_DOS_BOLETOS}${busca}`,
  );

  return <DetalheDoBoleto key={id} id={id} boletoDaLista={linha} aoFechar={fechar} />;
}

/*
 * O rodapé de ações.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ ESCONDER NÃO É AUTORIZAR.                                                │
 * │                                                                          │
 * │ O que decide aqui é a legibilidade da tela: mostrar "Cancelar boleto"    │
 * │ num boleto LIQUIDADO só produziria um `422` e uma pergunta. Quem recusa  │
 * │ é a API — e ela recusa mesmo que este código seja alterado no navegador. │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * "Ver PDF" e "Verificar agora" seguem o que a API diz (`pdf_disponivel`, `verificavel`), e
 * a linha da lista não traz nenhum dos dois: até o detalhe chegar, nada fica disponível.
 * Nenhuma das duas vira segundo título: uma lê o mesmo título, e a outra só antecipa a
 * consulta da conciliação.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A SONDA VEM PRIMEIRO, E SÓ DEPOIS REPROCESSAR.                           │
 * │                                                                          │
 * │ Quem decide é a API, por `sondavel`, `reprocessavel` e                   │
 * │ `reprocessamento_barrado_por`, e a tela compara com `true`: a linha da   │
 * │ lista não traz nenhum dos três, e até o detalhe chegar não há ação.      │
 * │                                                                          │
 * │ Outro gate barrando, nem sonda: ela gasta orçamento do provider para uma │
 * │ resposta que não muda nada. O boleto em envio incerto não tem gate, e a  │
 * │ sonda antecipa a próxima verificação dele. Um botão que sempre recusa    │
 * │ ensina o operador a insistir, e por isso o motivo mora numa `Dica`.      │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function Acoes({
  podeOperar,
  boleto,
  referencia,
  aoPedir,
  aoReprocessar,
}: {
  podeOperar: boolean;
  boleto: BoletoDoPainel;
  referencia: string;
  aoPedir: (pedido: PedidoDeConfirmacao) => void;
  aoReprocessar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const verPDF = useVerPDF(cliente, boleto.id);
  const verificar = useVerificarBoleto(cliente, boleto.id);
  const sondar = useSondarBoleto(cliente, boleto.id);
  const { esperando, esperar } = useEsperaDaRecusa();

  if (!podeOperar) {
    return (
      <p className="text-[12px] text-texto-3">Ações exigem papel de operador.</p>
    );
  }

  const { situacao, indeterminado } = boleto;
  const gate = boleto.reprocessamento_barrado_por;
  const reprocessavel = boleto.reprocessavel === true;
  const sondaResolve = gate === "sonda_necessaria" || (gate === undefined && !reprocessavel);
  const mostrarSonda = boleto.sondavel === true && sondaResolve;
  const emRecarga = esperando || boleto.sonda_em_recarga === true;
  const encerrado = ["LIQUIDADO", "BAIXADO", "DESCARTADO", "CANCELADO", "PROTESTADO"].includes(
    situacao,
  );

  async function abrirFicha() {
    try {
      const ficha = await verPDF.mutateAsync();
      if (ficha.via === "link") {
        // Link do provider, com o render fora: `noopener` tira de lá o acesso a esta aba.
        window.open(ficha.link, "_blank", "noopener,noreferrer");
      }
    } catch (falha) {
      avisos.falhou(recusaLegivel(falha));
    }
  }

  async function pedirVerificacao() {
    try {
      await verificar.mutateAsync();
      avisos.feito("Verificação pedida");
    } catch (falha) {
      avisos.falhou(recusaLegivel(falha));
    }
  }

  async function pedirSonda() {
    try {
      await sondar.mutateAsync();
      avisos.feito("Sonda pedida");
    } catch (falha) {
      const espera = esperaDaRecusa(falha);
      if (espera !== null) esperar(espera);
      avisos.falhou(fraseDaRecusa(falha));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1">
        <Botao
          tom="secundario"
          disabled={boleto.pdf_disponivel !== true}
          carregando={verPDF.isPending}
          onClick={() => void abrirFicha()}
        >
          <FileText aria-hidden="true" className="size-4" />
          Ver PDF
        </Botao>
        {boleto.pdf_disponivel === false && (
          <Dica texto="Sem ficha: o boleto ainda não foi emitido ou saiu de circulação." />
        )}
      </span>

      {boleto.verificavel === true && (
        <span className="flex items-center gap-1">
          <Botao
            tom="secundario"
            carregando={verificar.isPending}
            onClick={() => void pedirVerificacao()}
          >
            <RefreshCw aria-hidden="true" className="size-4" />
            Verificar agora
          </Botao>
          <Dica texto="A conciliação consulta o provider na rodada seguinte, e o resultado chega em Última consulta." />
        </span>
      )}

      {mostrarSonda && (
        <span className="flex items-center gap-1">
          <Botao
            tom="secundario"
            disabled={emRecarga}
            carregando={sondar.isPending}
            onClick={() => void pedirSonda()}
          >
            <Search aria-hidden="true" className="size-4" />
            Procurar título
          </Botao>
          <Dica
            texto={
              emRecarga
                ? "Sonda pedida. O resultado aparece aqui."
                : "A conciliação pergunta ao provider na rodada seguinte, sem emitir nada."
            }
          />
        </span>
      )}

      {reprocessavel && (
        <Botao tom="secundario" onClick={aoReprocessar}>
          <RotateCcw aria-hidden="true" className="size-4" />
          Reprocessar
        </Botao>
      )}

      {gate !== undefined && gate !== "sonda_necessaria" && (
        <span className="flex items-center gap-1 text-[12px] text-texto-3">
          Sem reprocessamento
          <Dica texto={fraseDoGate(gate)} />
        </span>
      )}

      {indeterminado && (
        <Botao
          tom="secundario"
          onClick={() =>
            aoPedir({
              severidade: "critica",
              titulo: `Resolver o indeterminado ${referencia}?`,
              corpo: (
                <>
                  Você declara que conferiu com o banco e que{" "}
                  <span className="dados">{situacao}</span> é a situação verdadeira.
                </>
              ),
              verbo: "Resolver",
              exigeJustificativa: true,
            })
          }
        >
          Resolver indeterminado
        </Botao>
      )}

      {!encerrado && (
        // Separado à direita, em vermelho contido (doc 06b §3).
        <Botao
          tom="destrutivo"
          className="ml-auto"
          onClick={() =>
            aoPedir({
              severidade: "destrutiva",
              titulo: `Cancelar o boleto ${referencia}?`,
              corpo: "Descarta ou baixa conforme o estado; se já registrado, o desfecho chega por evento.",
              verbo: "Cancelar boleto",
            })
          }
        >
          <Ban aria-hidden="true" className="size-4" />
          Cancelar boleto
        </Botao>
      )}
    </div>
  );
}
